/**
 * Contract for the per-item upload status surface — GET /api/surfaces/uploads (U9, upload spec
 * §5.5). The SPA's two consumers: the per-file status chip in Ask, and (with the `uploads` stream
 * frame in query.ts) the honest "still indexing" empty-state (D32).
 *
 * THE ROUTE'S REAL JSON IS THE SHAPE — no surfaceEnvelope, deliberately. The route
 * (app/api/surfaces/uploads/route.ts) answers `{ uploads: [...] }` on success and
 * `{ error: <code>, uploads: [] }` on every refusal, so `body.uploads.map(...)` never throws on
 * either path; wrapping the contract in the envelope would describe a wire shape that does not
 * exist. Schema-first: z.infer derives the types; .passthrough() so additive server fields never
 * fail the SPA's parse; .nullish() everywhere a value can be JSON null (the null-handling
 * invariant).
 *
 * HAND-SYNCED TWO-COPY FILE: the SPA's copy lives at .planning/integration/contracts/uploads.ts in
 * the dashboard repo (a SIBLING of Terum-Dashboard/, not inside it) and moves in the paired
 * Dashboard PR. The vocabularies below are pinned to lib/uploads/limits.ts by
 * __tests__/lib/uploads/uploads-contract-sync.test.ts — edit them only together.
 */

import { z } from "zod";

// `user_uploads.status` — the closed state machine (mirrors migration 169's CHECK, owned by
// lib/uploads/limits.ts UPLOAD_STATUSES). CLOSED on purpose, unlike `failureReason` below: a new
// status is a state-machine change the SPA must render deliberately (which chip? spinner or
// terminal?), so it is a coordinated two-repo change by definition and the parse failing loudly in
// dev is the tripwire that enforces it.
export const UploadStatusSchema = z.enum([
  "queued",
  "extracting",
  "distilling",
  "ready",
  "failed",
  "rejected",
]);
export type UploadStatus = z.infer<typeof UploadStatusSchema>;

// The subset of statuses that mean "still indexing" — mirrors UPLOAD_IN_FLIGHT_STATUSES in
// lib/uploads/limits.ts (same sync test). `failed`/`rejected` are terminal: render them as
// failures with their typed reason, never as a spinner.
export const UPLOAD_IN_FLIGHT_STATUSES = ["queued", "extracting", "distilling"] as const;

// The failure vocabulary the server emits today (lib/uploads/limits.ts UPLOAD_FAILURE_REASONS).
// The FIELD is an open string: reasons are server-owned diagnostics, a new one can ship
// server-first, and an unknown reason must degrade to a generic failure label — not fail the parse
// and blank the whole list. This const is the SPA's reason→label map key set, not a validator.
export const KNOWN_UPLOAD_FAILURE_REASONS = [
  "too_many_files",
  "request_too_large",
  "too_large",
  "too_small",
  "unsupported_type",
  "content_mismatch",
  "unsupported_encoding",
  "daily_file_limit",
  "daily_byte_limit",
  "storage_write_failed",
  "db_write_failed",
  "capture_paused",
  "no_text_layer",
  "extraction_failed",
  "extracted_too_large",
  "partial_ocr",
  "distill_failed",
] as const;
export type KnownUploadFailureReason = (typeof KNOWN_UPLOAD_FAILURE_REASONS)[number];

// One row of the GET response — field-for-field the route's mapping of a user_uploads row.
// `chatId` null = an unadopted pre-chat drop; `failureReason` non-null only alongside
// status:"failed"/"rejected"; `extractedChars` null until extraction has run.
export const UploadItemSchema = z
  .object({
    uploadId: z.string().min(1),
    chatId: z.string().nullish(),
    filename: z.string(),
    mimeType: z.string(),
    byteSize: z.number(),
    status: UploadStatusSchema,
    failureReason: z.string().nullish(),
    extractedChars: z.number().nullish(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .passthrough();
export type UploadItem = z.infer<typeof UploadItemSchema>;

// The whole GET body, both shapes: `uploads` is ALWAYS present (empty on refusal); `error` is the
// route's typed refusal code when present (open string for the same server-first reason as
// failureReason). Newest-first, hard-capped at 50 by the route with no hasMore signal — a chat
// with more uploads truncates silently, which the route owns, not this schema.
export const UploadsListResponseSchema = z
  .object({
    uploads: z.array(UploadItemSchema).default([]),
    error: z.string().nullish(),
  })
  .passthrough();
export type UploadsListResponse = z.infer<typeof UploadsListResponseSchema>;
